/**
 * Wave 24B — keep PLP Filter/Sort toolbar pinned under sticky header while the
 * product list still has room below (Flutter pinned sliver until near list end).
 * Prestige position:sticky unsticks with the section as soon as the footer
 * region enters; this widens the pin window to the product-list bottom.
 * Applies at every viewport width — this is Flutter behavior parity, not a
 * mobile-only chrome pattern, and the pin/unpin math is width-agnostic.
 */
(function () {
  'use strict';

  var PIN_CLASS = 'eros-plp-toolbar--pinned';
  var PLACEHOLDER_ATTR = 'data-eros-plp-toolbar-ph';

  /*
   * --sticky-area-height (css-variables.liquid:63) is a calc() expression.
   * getComputedStyle(...).getPropertyValue on a *custom* property returns
   * its literal specified text ("calc(...)"), not the resolved pixel value
   * — parseFloat on that always yields NaN. Resolve the real used value by
   * applying it to a length-resolving property (top) on a hidden probe
   * element instead; getComputedStyle DOES resolve calc() to used-value px
   * for ordinary properties. Re-read every call (not cached) since the
   * variable itself shifts live as the header collapses/expands.
   */
  var _stickyProbe = null;
  function stickyTopPx() {
    if (!_stickyProbe) {
      _stickyProbe = document.createElement('div');
      _stickyProbe.setAttribute('aria-hidden', 'true');
      _stickyProbe.style.cssText =
        'position:fixed;visibility:hidden;pointer-events:none;left:0;width:0;height:0;top:var(--sticky-area-height);';
      document.body.appendChild(_stickyProbe);
    }
    var n = parseFloat(getComputedStyle(_stickyProbe).top);
    return isFinite(n) ? n : 0;
  }

  function findToolbar(root) {
    return (root || document).querySelector(
      '.shopify-section--main-collection .collection-toolbar.eros-plp-toolbar, ' +
        '.shopify-section--main-search .collection-toolbar.eros-plp-toolbar'
    );
  }

  function findList(toolbar) {
    if (!toolbar) return null;
    var section = toolbar.closest('.shopify-section');
    if (!section) return null;
    return (
      section.querySelector('product-list.product-list, .product-list:not(.product-list--carousel)') ||
      section.querySelector('.collection__main') ||
      section.querySelector('.collection')
    );
  }

  function ensurePlaceholder(toolbar) {
    var next = toolbar.nextElementSibling;
    if (next && next.getAttribute(PLACEHOLDER_ATTR) === '1') return next;
    var ph = document.createElement('div');
    ph.setAttribute(PLACEHOLDER_ATTR, '1');
    ph.setAttribute('aria-hidden', 'true');
    ph.style.cssText = 'display:none;margin:0;padding:0;border:0;';
    toolbar.insertAdjacentElement('afterend', ph);
    return ph;
  }

  function unpin(toolbar, ph) {
    if (!toolbar || !toolbar.classList.contains(PIN_CLASS)) return;
    toolbar.classList.remove(PIN_CLASS);
    toolbar.style.removeProperty('position');
    toolbar.style.removeProperty('top');
    toolbar.style.removeProperty('left');
    toolbar.style.removeProperty('right');
    toolbar.style.removeProperty('width');
    toolbar.style.removeProperty('z-index');
    toolbar.style.removeProperty('margin-left');
    toolbar.style.removeProperty('margin-right');
    if (ph) {
      ph.style.display = 'none';
      ph.style.height = '0px';
      ph.style.marginLeft = '0px';
      ph.style.marginRight = '0px';
    }
  }

  function pin(toolbar, ph, top) {
    // Once pinned (position:fixed), the toolbar's own getBoundingClientRect
    // reflects wherever it's already fixed to — not its natural document-flow
    // width/offset. pin() re-runs on every scroll/resize while pinned, so
    // reading left/width from the toolbar itself froze them at whatever they
    // were the moment it first pinned; resizing/rotating while scrolled left
    // it visibly misaligned against the grid below until it fully unpinned
    // and re-pinned. The placeholder stays in normal flow throughout, so it
    // always reflects the current natural position — read from it instead
    // once already pinned. Confirmed via audit + live measurement 2026-08-09.
    var alreadyPinned = toolbar.classList.contains(PIN_CLASS);
    // The toolbar is .full-bleed below the desktop width: negative inline
    // margins stretch it to the viewport. A fixed box keeps those margins, so
    // it slid left by the gutter and left a gap on the right (RTL). The
    // placeholder takes the margins over (same natural box) and the pinned
    // toolbar drops them — left/width alone place it. Owner report 2026-10-06.
    if (!alreadyPinned && ph) {
      var natural = getComputedStyle(toolbar);
      ph.style.marginLeft = natural.marginLeft;
      ph.style.marginRight = natural.marginRight;
    }
    var rect = alreadyPinned && ph ? ph.getBoundingClientRect() : toolbar.getBoundingClientRect();
    var width = rect.width;
    var height = alreadyPinned && ph ? toolbar.getBoundingClientRect().height : rect.height;
    if (ph) {
      ph.style.display = 'block';
      ph.style.height = height + 'px';
    }
    toolbar.classList.add(PIN_CLASS);
    toolbar.style.position = 'fixed';
    toolbar.style.top = top + 'px';
    toolbar.style.left = rect.left + 'px';
    toolbar.style.right = 'auto';
    toolbar.style.width = width + 'px';
    toolbar.style.zIndex = '5';
    toolbar.style.marginLeft = '0px';
    toolbar.style.marginRight = '0px';
  }

  function update() {
    var toolbar = findToolbar(document);
    if (!toolbar) return;
    var ph = ensurePlaceholder(toolbar);

    var list = findList(toolbar);
    if (!list) {
      unpin(toolbar, ph);
      return;
    }

    var top = stickyTopPx();
    var naturalTop = toolbar.classList.contains(PIN_CLASS)
      ? ph.getBoundingClientRect().top
      : toolbar.getBoundingClientRect().top;
    var listBottom = list.getBoundingClientRect().bottom;
    var toolbarH = toolbar.getBoundingClientRect().height || 64;
    /* Unpin when list end is near the sticky band (Flutter: near list end). */
    var stillHasList = listBottom > top + toolbarH + 24;
    var shouldPin = naturalTop <= top + 0.5 && stillHasList;

    if (shouldPin) {
      pin(toolbar, ph, top);
    } else {
      unpin(toolbar, ph);
    }
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      update();
    });
  }

  function init() {
    if (!findToolbar(document)) return;
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('facet:update', schedule);
    document.addEventListener('shopify:section:load', schedule);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
